import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Building2, Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { meQueryOptions, useSwitchOrg } from '~/api/auth';
import { isApiError } from '~/api/client';
import { type McpConsentRequest, mcpConsentQueryOptions, useDecideMcpConsent } from '~/api/mcp';
import { AuthCard } from '~/app/AuthCard';
import { Button } from '~/ui/Button';
import { Notice } from '~/ui/Notice';

/**
 * Claude's sign-in (the API's docs/MCP.md, "Signing in from claude.ai"). claude.ai opens
 * this page with an OAuth authorization request; the person signs in as usual, and an
 * owner allows Claude, which sends them back to Claude with a one-time code. Outside the
 * app shell, like sign-in. The query parameters keep OAuth's own snake_case names.
 */
interface ConsentSearch {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  code_challenge: string;
  code_challenge_method: string;
  state?: string;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export const Route = createFileRoute('/connect/claude')({
  validateSearch: (s: Record<string, unknown>): ConsentSearch => ({
    client_id: str(s['client_id']),
    redirect_uri: str(s['redirect_uri']),
    response_type: str(s['response_type']),
    code_challenge: str(s['code_challenge']),
    code_challenge_method: str(s['code_challenge_method']),
    ...(typeof s['state'] === 'string' ? { state: s['state'] } : {}),
  }),
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQueryOptions);
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        throw redirect({ to: '/login', search: { next: location.href } });
      }
      throw error;
    }
  },
  component: ConnectClaudePage,
});

function ConnectClaudePage() {
  const search = Route.useSearch();
  const request: McpConsentRequest = {
    clientId: search.client_id,
    redirectUri: search.redirect_uri,
    responseType: search.response_type,
    codeChallenge: search.code_challenge,
    codeChallengeMethod: search.code_challenge_method,
    ...(search.state !== undefined ? { state: search.state } : {}),
  };
  const me = useQuery(meQueryOptions);
  const check = useQuery(mcpConsentQueryOptions(request));
  const decide = useDecideMcpConsent();
  const switchOrg = useSwitchOrg();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    document.title = 'Connect Claude · 11Deeds';
  }, []);

  if (!me.data || check.isPending) {
    return (
      <AuthCard title="Connect Claude">
        <p className="text-ink-3">One moment…</p>
      </AuthCard>
    );
  }

  if (check.isError) {
    return (
      <AuthCard title="Connect Claude">
        <Notice tone="late">
          {isApiError(check.error)
            ? check.error.message
            : 'This sign-in link is not valid. Start connecting again from Claude.'}
        </Notice>
      </AuthCard>
    );
  }

  const { person, workspace, memberships } = me.data;
  const { clientName, redirectHost } = check.data;
  const otherOwned = memberships.filter(
    (m) => m.role === 'owner' && m.workspaceId !== workspace.id,
  );
  const busy = decide.isPending || leaving || switchOrg.isPending;

  const answer = (allow: boolean) =>
    decide.mutate(
      { ...request, allow },
      {
        onSuccess: ({ redirectTo }) => {
          setLeaving(true);
          window.location.assign(redirectTo);
        },
      },
    );

  const openOrg = async (workspaceId: string) => {
    await switchOrg.mutateAsync(workspaceId);
    // The same request, now in the other company.
    window.location.reload();
  };

  return (
    <AuthCard
      title={`Connect ${clientName} to 11Deeds`}
      subtitle={`${workspace.name}, signed in as ${person.name}`}
    >
      {person.role === 'owner' ? (
        <div className="flex flex-col gap-4 text-sm text-ink-2">
          <p>{clientName} will work in 11Deeds as you. It will be able to:</p>
          <ul className="flex flex-col gap-2">
            {[
              'read the company’s people, teams, work, time, attendance and reports',
              'create and change tasks, comment, post project updates and decide reports, when you ask it to',
            ].map((line) => (
              <li key={line} className="flex gap-2">
                <Check size={16} className="mt-0.5 shrink-0 text-accent" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p>
            Every change is recorded under your name. Disconnect it any time in Settings, Claude
            (AI). After you choose, you go back to <b className="text-ink">{redirectHost}</b>.
          </p>
          {decide.isError && (
            <Notice tone="late">
              {isApiError(decide.error) ? decide.error.message : 'Something went wrong. Try again.'}
            </Notice>
          )}
          <div className="flex flex-col gap-2">
            <Button block loading={decide.isPending || leaving} onClick={() => answer(true)}>
              Allow
            </Button>
            <Button block variant="quiet" disabled={busy} onClick={() => answer(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 text-sm">
          <Notice tone="warn">
            Only an owner of {workspace.name} can connect Claude. Your role here is{' '}
            {person.role.replace('_', ' ')}.
          </Notice>
          {otherOwned.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-ink-2">You own another company. Connect that one instead:</p>
              {otherOwned.map((m) => (
                <Button
                  key={m.workspaceId}
                  variant="secondary"
                  block
                  disabled={busy}
                  icon={<Building2 size={16} aria-hidden />}
                  onClick={() => void openOrg(m.workspaceId)}
                >
                  {m.name}
                </Button>
              ))}
            </div>
          )}
          <Button variant="quiet" block disabled={busy} onClick={() => answer(false)}>
            Back to {redirectHost}
          </Button>
        </div>
      )}
    </AuthCard>
  );
}
